import { useAskLevel } from "@/hooks/useAskLevel";
import { QueryClientContext } from "@tanstack/react-query";
import { planQueryKey } from "@/hooks/usePlan";
import { AskAllowanceCard, AskAllowanceNote, useAskAllowance } from "@/components/plan/AskAllowance";
import { AskLevelAck, AskLevelCard, AskLevelControl } from "./askLevel/AskLevel";
import i18n from "i18next";
import { Trans } from "react-i18next";
/**
 * Ask Lilia — the in-editor natural-language front door over the skill family.
 *
 * One input: the message is routed server-side to a skill (citations,
 * compile-doctor, equation, table, …) and run through the governed AI path; the
 * reply carries prose + the artifact (LML / BibTeX / LaTeX in fenced blocks).
 * Each fenced block gets Copy, and a LaTeX fence gets "Apply" (inserts an
 * equation block via the host). Proficiency (beginner/intermediate/advanced)
 * tunes the answer and is remembered. A 403 renders the locked/upsell state.
 *
 * Backend: api.ai.ask (POST /api/ai/ask). The reply may reference KB articles
 * (the server runs search_kb/get_kb), e.g. "see Data → LaTeX table — /help/latex-table".
 */
import { takeAskPrefill } from "@/lib/askPrefill";
import { useEditorStore } from "@/store/editorStore";
import { ASK_MATH_PHOTO_EVENT, isMathReadIntent, takeAskMathPhoto } from "@/lib/askMath";
import { MathProposalView, type MathTurn } from "./askMath/MathProposal";
import {
  FIGURE_LOCK_REASONS,
  buildFigureRequest,
  intentFor,
  requestFigureAction,
  setPendingChange,
} from "@/lib/tikzAsk";
import {
  FigureContextChip,
  FigureProposalView,
  FigureSuggestions,
  figureTurnForStorage,
  pendingFrom,
  useFigureScope,
  type FigureTurn,
} from "./askFigure/AskFigure";
import { useDocumentPreamble } from "./nodeviews/tikz/useDocumentPreamble";
import { useCallback, useContext, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { RefreshButton } from "@/components/shared/refreshButton/RefreshButton";
import { useCanEdit } from "@/lib/documentAccess";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/lilia-ui/dropdown-menu";
import { canKeepOnTop } from "@/lib/askBridge";
import { useNavigate } from "react-router";
import ReactMarkdown from "react-markdown";
import { withAskContext, type AskContext } from "@/lib/mobileReader";
import { AI_CHANGES_EVENT, firstSentence } from "@/lib/mobileChanges";
import { MathText, protectInlineMath, protectedMath } from "@/components/editor/askFigure/MathText";
import { CodeFence, fenceLanguage } from "./CodeFence";
import remarkGfm from "remark-gfm";
import {
  IconSparkles,
  IconSend,
  IconX,
  IconAppWindow,
  IconLayoutColumns,
  IconPictureInPictureOn,
  IconArrowBarToLeft,
  IconLock,
  IconBolt,
  IconPencil,
  IconArrowBackUp,
  IconPlus,
  IconMessageCircle,
  IconTrash,
  IconChevronDown,
  IconCopy,
  IconArrowRight,
  IconLayoutSidebarRight,
  IconLayoutSidebarLeftExpand,
  IconLayoutSidebarRightExpand,
  IconPictureInPicture,
  IconPaperclip,
  IconPhoto,
  IconCamera,
  IconMathFunction,
  IconFileText,
  IconWorld,
} from "@tabler/icons-react";

import { api, AiArchitectLockedError, type Proficiency, type BlockOp, type Document } from "@/lib/api";
import { applyArchitectOps, summarizeArchitectOps, isDocBuildIntent } from "@/lib/applyArchitectOps";
import {
  type StoredConversation,
  loadConversations,
  saveConversations,
  loadActiveId,
  saveActiveId,
  newConversationId,
  titleFromTurns,
} from "@/lib/askConversations";
import {
  detectEditorCommands,
  runEditorCommand,
  EDITOR_COMMANDS,
  type EditorCommand,
} from "@/lib/editorCommands";
import { Textarea } from "@/components/lilia-ui/textarea";
import { Spinner } from "@/components/lilia-ui/spinner";
import { cn } from "@/lib/cn";
import { testIds } from "@/testIds";


// One starter per skill, worded with the router's own triggers so each lands on
// its skill (the user, 11 Oct: suggestions "not adapted to Lilia's skills").
// The API's AskSuggestionRoutingTests holds the same strings: change both.
// In the interface's language (editor:ask.suggest.*); the API's router knows
// the French and Spanish ones too (AskSuggestionRoutingTests).
const SUGGESTIONS = [
  "outline", // lilia-document-architect
  "cite", // lilia-citations
  "compile", // lilia-compile-doctor
  "equation", // lilia-equation
  "table", // lilia-table
  "figure", // lilia-figure
  "polish", // lilia-polish
  "journal", // lilia-journal
];

// A reader who can't edit (view only): questions about the document, since
// Lilia answers but changes nothing for them (the API refuses edit mode too).
// All four route to lilia-tutor.
const READER_SUGGESTIONS = ["explainDoc", "explainResult", "equations", "sources"];

// Library-scoped prompts — Ask Lilia reasons ACROSS the library (shell &
// documents handoff §4) when there's no open document.
const LIBRARY_SUGGESTIONS = ["unfinished", "issues", "closest", "euclid"];

// Challenge (19 Oct, API #356): Ask Lilia questions the author on their document. Sent at once.
const CHALLENGE_SKILL = "lilia-challenge";
const CHALLENGES = ["defence", "reviewer", "quiz"] as const;

// ── Composer attachments (ds30 §6) ──────────────────────────────────────────
// Accepted types + ceiling. (§16 Q4 left max-size open — 10MB is the working
// default; revisit with Claude Design.) Images preview as thumbnails; the rest
// (data/source/docs) render as removable chips.
const ATTACH_IMAGE_EXT = ["png", "jpg", "jpeg", "webp", "svg"];
const ATTACH_FILE_EXT = ["csv", "tex", "bib", "docx", "pdf"];
const ATTACH_ACCEPT_IMAGE = ".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml";
const ATTACH_ACCEPT_FILE = ".csv,.tex,.bib,.docx,.pdf";
const ATTACH_MAX_BYTES = 10 * 1024 * 1024;
const ATTACH_MAX_IMAGE_BYTES = 5 * 1024 * 1024; // Anthropic's per-image base64 cap
const ATTACH_MIN_IMAGE_EDGE = 32; // px — Anthropic rejects degenerate images (a 1×1 → "Could not process image")

interface Attachment {
  id: string;
  name: string;
  kind: "image" | "file";
  sizeLabel: string;
  previewUrl?: string; // object URL for image thumbnails
  status: "ready" | "uploading" | "error";
  error?: string;
  file?: File; // kept so send() can read text-extractable content into the message
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Text-extractable attachments are read client-side and inlined into the message
// so the file actually enters the conversation. Binary types (docx/pdf/images)
// await the backend attachment pipeline; we flag them inline rather than drop them.
const ATTACH_TEXT_EXT = ["csv", "tsv", "tex", "bib", "txt", "md", "markdown", "json", "log"];
const MAX_INLINE_CHARS = 100_000;

function isTextExtractable(a: Attachment): boolean {
  const ext = (a.name.split(".").pop() || "").toLowerCase();
  return ATTACH_TEXT_EXT.includes(ext) || (a.file?.type.startsWith("text/") ?? false);
}

// Read staged text files and append them (fenced, labeled) to the typed message.
async function buildMessageWithAttachments(text: string, staged: Attachment[]): Promise<string> {
  if (staged.length === 0) return text;
  const blocks: string[] = [];
  for (const a of staged) {
    if (a.file && isTextExtractable(a)) {
      try {
        let content = await a.file.text();
        if (content.length > MAX_INLINE_CHARS) content = content.slice(0, MAX_INLINE_CHARS) + "\n…(truncated)";
        blocks.push(`Attached file "${a.name}":\n\n\`\`\`\n${content}\n\`\`\``);
      } catch {
        blocks.push(`[Attached "${a.name}" — could not be read.]`);
      }
    } else {
      blocks.push(`[Attached "${a.name}" — this file type isn't readable yet; convert it to text/CSV or paste its contents.]`);
    }
  }
  return blocks.length ? `${text}\n\n${blocks.join("\n\n")}` : text;
}

// Binary/image files are sent to the backend as base64 (images/PDFs go to the
// model natively; docx is extracted server-side).
function guessMime(name: string): string {
  const ext = (name.split(".").pop() || "").toLowerCase();
  const map: Record<string, string> = {
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp",
    svg: "image/svg+xml", gif: "image/gif", pdf: "application/pdf",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  };
  return map[ext] || "application/octet-stream";
}

// Decode an image URL to read its pixel dimensions (for the min-size guard).
function readImageSize(url: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => reject(new Error("decode failed"));
    img.src = url;
  });
}

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunk = 0x8000; // avoid stack overflow on large files
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function buildAttachments(staged: Attachment[]): Promise<{ name: string; mediaType: string; dataBase64: string }[]> {
  const out: { name: string; mediaType: string; dataBase64: string }[] = [];
  for (const a of staged) {
    if (!a.file) continue;
    try {
      out.push({ name: a.name, mediaType: a.file.type || guessMime(a.name), dataBase64: await fileToBase64(a.file) });
    } catch { /* skip unreadable */ }
  }
  return out;
}

/** A source the assistant cited from live web search (mirrors the API shape). */
type WebCitation = { title?: string; url: string; snippet?: string };

type Turn =
  | { role: "user"; text: string; files?: string[] }
  | { role: "lilia"; skillName: string; reply: string; commands: EditorCommand[]; ops?: BlockOp[]; undoVersionId?: string; documentId?: string; citations?: WebCitation[]; partialApply?: boolean; model?: string; figure?: FigureTurn; math?: MathTurn; skillId?: string };

interface AskLiliaPanelProps {
  onClose: () => void;
  /** Insert an equation block from an applied LaTeX fence (host-wired). */
  onApplyLatex?: (latex: string) => void | Promise<void>;
  /** Prefill the composer (e.g. from a selection / "Fix this" action). */
  initialMessage?: string;
  /** The open document — passed to the API so Ask Lilia grounds answers in it. */
  documentId?: string;
  /** Current dock vs overlay presentation (host-owned). */
  mode?: "dock" | "overlay";
  /** Toggle dock⇄overlay. When omitted, the header toggle is hidden. */
  onToggleMode?: () => void;
  /** Which side the dock sits on (Block-mode opt-in; default right). */
  side?: "right" | "left";
  /** Toggle dock side right⇄left. Block-only; when omitted, hidden. */
  onToggleSide?: () => void;
  /** Context sub-line under the title (handoff §4). Defaults to "about this
   *  document" when documentId is set, else "across your library". */
  contextLabel?: string;
  /** Library docs (id + title) for resolving reference chips in library-scoped
   *  answers (handoff §4 — answers carry clickable doc references). */
  libraryDocs?: { id: string; title: string }[];
  /**
   * Chat-First: escape hatch from Primary (chat-main) → Side (doc primary +
   * docked Ask Lilia). When set, a "Move to the side instead" link shows under
   * the composer (handoff 2026-07-15).
   */
  onMoveToSide?: () => void;
  /** When true, hide the panel close control (chat is the primary surface). */
  primarySurface?: boolean;
  /**
   * This pane's surface strip, rendered at the right of the header.
   *
   * Both panes carry one and both headers read the same way: what you are
   * looking at on the left, what you could look at instead on the right.
   */
  headerTabs?: React.ReactNode;
  /**
   * The document's title, at the left of the header.
   *
   * Ask Lilia can be in either pane now, so its header has to say which
   * document the conversation is about, the same way the other surfaces do.
   */
  /**
   * When false, skip server conversation sync (list/hydrate). Used by the
   * keep-alive dual mount so only the *active* Flow/Block surface hits
   * `/api/ai/conversations` (T1 fan-out). Default true.
   */
  syncEnabled?: boolean;
  /**
   * The block the reader tapped (mobile reader): shown as a removable chip, and
   * the message carries it so Ask Lilia scopes its edits to that block.
   */
  blockContext?: AskContext | null;
  onClearBlockContext?: () => void;
  /** Focus the input when the panel opens (the mobile sheet: the keyboard opens only here). */
  autoFocusInput?: boolean;
  /**
   * In the mobile reader's sheet (Olivia, 10 Oct): no header (the sheet has New chat
   * and close) and no "Edit this document" switch: it edits if you can edit.
   */
  phoneSheet?: boolean;
  /**
   * Whether the reader may edit, when no editor is on the page to say (Ask Lilia
   * in its own window). Defaults to the open document's role.
   */
  canEdit?: boolean;
  /**
   * Move Ask Lilia out of the editor (Olivia, 10 Oct): one icon before ✕, a menu
   * with Open in new window and, where the browser can, Keep on top.
   */
  onMove?: (how: "window" | "top") => void;
  /** In its own window: "Bring back to editor" in place of the move icon. */
  onBringBack?: () => void;
  /**
   * Where Ask Lilia sits in the editor: half and half beside the document
   * ("chat"), or docked at the side. The Move menu offers the other one first
   * (Olivia, 10 Oct: one menu for where Ask Lilia lives).
   */
  arrangement?: "chat" | "side";
  onArrange?: (next: "chat" | "side") => void;
}

/** Start a new conversation from outside the panel (the phone sheet's New chat). */
export const ASK_NEW_CHAT_EVENT = "lilia:ask-new-chat";

/** Short label for a catalog model id (e.g. claude-sonnet-4-6 → Sonnet 4.6). */
function formatModelLabel(modelId: string): string {
  const id = (modelId || "").trim();
  if (!id) return id;
  // claude-sonnet-4-6 / claude-opus-4-… / claude-haiku-…
  const m = id.match(/^claude-([a-z]+)-(\d+(?:-\d+)?)/i);
  if (m) {
    const family = m[1].charAt(0).toUpperCase() + m[1].slice(1);
    const ver = m[2].replace(/-/g, ".");
    return `${family} ${ver}`;
  }
  // gpt-4o-mini, o3-mini, etc. — keep compact id
  if (id.length <= 22) return id;
  return id.slice(0, 20) + "…";
}

/** Find library documents the answer names (the model quotes exact titles). */
function findReferencedDocs(reply: string, docs: { id: string; title: string }[]): { id: string; title: string }[] {
  const lower = reply.toLowerCase();
  const refs: { id: string; title: string }[] = [];
  const seen = new Set<string>();
  for (const d of docs) {
    const t = (d.title ?? "").trim();
    if (t.length < 3 || seen.has(d.id)) continue;
    if (lower.includes(t.toLowerCase())) {
      seen.add(d.id);
      refs.push({ id: d.id, title: t });
      if (refs.length >= 6) break;
    }
  }
  return refs;
}


export function AskLiliaPanel({ onClose, onApplyLatex, initialMessage, documentId, mode, onToggleMode, side, onToggleSide, contextLabel, libraryDocs, onMoveToSide, primarySurface, headerTabs, syncEnabled = true, blockContext = null, onClearBlockContext, autoFocusInput = false, phoneSheet = false, canEdit: canEditProp, onMove, onBringBack, arrangement, onArrange }: AskLiliaPanelProps) {
  const navigate = useNavigate();
  // Library scope = no open document → reason across the library (handoff §4).
  const isLibrary = !documentId;
  const contextSub = contextLabel ?? (documentId ? i18n.t("editor:ask.aboutDoc") : i18n.t("editor:ask.acrossLibrary"));
  // ── ChatGPT-style conversation management (per document, persisted) ──
  const [conversations, setConversations] = useState<StoredConversation<Turn>[]>(
    () => loadConversations<Turn>(documentId),
  );
  // Opening a document lands in ITS conversation, or a fresh one.
  //
  // The fallback used to be "most recently updated conversation anywhere",
  // which meant opening a document dropped you into another document's chat —
  // the list is deliberately de-siloed, so "most recent" is rarely yours. Now
  // the fallback is scoped, and when a document has no conversation of its own
  // the answer is none: the header already reads "New chat" and setTurns
  // creates one on the first message, so a fresh document starts fresh without
  // littering the list with empty conversations nobody wrote in.
  //
  // Switching conversations is untouched — every conversation is still in the
  // picker, including other documents' and the library's.
  const pickForScope = useCallback((list: StoredConversation<Turn>[], scope: string | null) => {
    const saved = loadActiveId(scope ?? undefined);
    if (saved && list.some((c) => c.id === saved)) return saved;
    const mine = list.filter((c) => (c.documentId ?? null) === scope);
    if (!mine.length) return null;
    return [...mine].sort((a, b) => b.updatedAt - a.updatedAt)[0]!.id;
  }, []);

  const [activeId, setActiveId] = useState<string | null>(() =>
    pickForScope(loadConversations<Turn>(documentId), documentId ?? null),
  );
  const [convMenuOpen, setConvMenuOpen] = useState(false);
  // Re-pick when the open document changes. The initialiser above only runs on
  // mount, and navigating document → document keeps this panel mounted, so
  // without this you would carry the previous document's conversation across.
  // Guarded on the id actually changing, so it never overrides a conversation
  // the author chose while staying on one document.
  const scopeRef = useRef<string | null>(documentId ?? null);
  useEffect(() => {
    const scope = documentId ?? null;
    if (scopeRef.current === scope) return;
    scopeRef.current = scope;
    setActiveId(pickForScope(conversationsRef.current, scope));
  }, [documentId, pickForScope]);



  const activeConv = conversations.find((c) => c.id === activeId) ?? null;


  // Rename the open conversation. Local state moves on every keystroke so the
  // field stays responsive; the server hears about it once typing stops,
  // because a PATCH per character is a lot of requests to say one thing.
  const renameTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const renameActiveConversation = useCallback((title: string) => {
    if (!activeId) return;

    const next = conversationsRef.current.map((c) =>
      c.id === activeId ? { ...c, title, titleLocked: true, updatedAt: Date.now() } : c,
    );
    conversationsRef.current = next;
    saveConversations(documentId, next);   // was state-only: the name died on reload
    setConversations(next);

    const serverId = conversationsRef.current.find((c) => c.id === activeId)?.serverId;
    if (!serverId) return;                     // not synced yet — the next sync carries it

    if (renameTimer.current) clearTimeout(renameTimer.current);
    renameTimer.current = setTimeout(() => {
      void api.ai.conversations.rename(serverId, title).catch(() => {
        // A failed rename is not worth interrupting the conversation for: the
        // local title stands and the next sync reconciles it.
      });
    }, 700);
  }, [activeId, documentId]);

  useEffect(() => () => {
    if (renameTimer.current) clearTimeout(renameTimer.current);
  }, []);

  // In-place rename (design handoff §2 option 1d·A). Entering edit swaps the
  // switcher for a field in the same 30px control, so the "+" and the view
  // tabs do not move.
  const [renamingTitle, setRenamingTitle] = useState(false);
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!renamingTitle) return;
    const el = renameInputRef.current;
    if (!el) return;
    el.focus();
    el.select();                       // §4.4: pre-selected
  }, [renamingTitle]);

  const commitRename = useCallback((raw: string) => {
    setRenamingTitle(false);
    // §4.4: newlines stripped, trimmed, 120 max; blank reverts to the
    // auto-name rather than saving an empty title.
    const next = raw.replace(/[\r\n]+/g, " ").trim().slice(0, 120);
    if (!next) return;
    renameActiveConversation(next);
  }, [renameActiveConversation]);
  const turns = activeConv?.turns ?? [];

  // Scope helpers (de-siloed list): a conversation belongs to a document or the
  // library; show a chip only when it differs from the open scope.
  const curScope = documentId ?? null;
  const docTitleFor = (cid: string | null | undefined) =>
    cid ? (libraryDocs?.find((d) => d.id === cid)?.title ?? "Another document") : "Library";
  const scopeChip = (cid: string | null | undefined) =>
    (cid ?? null) === curScope ? null : docTitleFor(cid);
  // The active conversation is from a different scope than the open one → offer
  // to continue it here (promote) or start fresh.
  const scopeMismatch = !!activeConv && (activeConv.documentId ?? null) !== curScope;

  // Refs mirror state and are updated SYNCHRONOUSLY by the mutators below, so the
  // async server helpers (persistTurn) always see the active conversation that
  // was just created in the same event — no stale-closure / double-create race.
  const conversationsRef = useRef(conversations);
  const activeIdRef = useRef(activeId);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const commitConversations = useCallback((next: StoredConversation<Turn>[]) => {
    conversationsRef.current = next;
    saveConversations(documentId, next);
    setConversations(next);
  }, [documentId]);
  const commitActiveId = useCallback((id: string | null) => {
    activeIdRef.current = id;
    saveActiveId(documentId, id);
    setActiveId(id);
  }, [documentId]);

  // Replace the active conversation's turns (creating one on first message).
  const setTurns = useCallback((updater: Turn[] | ((prev: Turn[]) => Turn[])) => {
    let list = conversationsRef.current;
    let id = activeIdRef.current;
    if (!id || !list.some((c) => c.id === id)) {
      id = newConversationId();
      list = [{ id, title: i18n.t("editor:ask.newChat"), turns: [], updatedAt: Date.now(), documentId: documentId ?? null }, ...list];
      activeIdRef.current = id;
      setActiveId(id);
      saveActiveId(documentId, id);
    }
    const next = list.map((c) => {
      if (c.id !== id) return c;
      const nextTurns = typeof updater === "function" ? (updater as (p: Turn[]) => Turn[])(c.turns) : updater;
      return { ...c, title: titleFromTurns(nextTurns), updatedAt: Date.now(), turns: nextTurns };
    });
    commitConversations(next);
  }, [documentId, commitConversations]);

  const newChat = useCallback(() => {
    setConvMenuOpen(false);
    setError(null);
    const id = newConversationId();
    commitConversations([{ id, title: i18n.t("editor:ask.newChat"), turns: [] as Turn[], updatedAt: Date.now(), documentId: documentId ?? null }, ...conversationsRef.current]);
    commitActiveId(id);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [commitConversations, commitActiveId, documentId]);
  useEffect(() => {
    const onNew = () => newChat();
    window.addEventListener(ASK_NEW_CHAT_EVENT, onNew);
    return () => window.removeEventListener(ASK_NEW_CHAT_EVENT, onNew);
  }, [newChat]);

  const switchChat = useCallback((id: string) => {
    commitActiveId(id);
    setConvMenuOpen(false);
  }, [commitActiveId]);

  const deleteChat = useCallback((id: string) => {
    const target = conversationsRef.current.find((c) => c.id === id);
    if (target?.serverId) void api.ai.conversations.remove(target.serverId).catch(() => {});
    const next = conversationsRef.current.filter((c) => c.id !== id);
    commitConversations(next);
    if (activeIdRef.current === id) {
      commitActiveId(next.length ? [...next].sort((a, b) => b.updatedAt - a.updatedAt)[0].id : null);
    }
  }, [commitConversations, commitActiveId]);

  // ── Move / clone a conversation across documents ──
  // Picker target: which conversation + whether we're moving or copying.
  const [picker, setPicker] = useState<{ convId: string; mode: "move" | "copy" } | null>(null);
  const [docOptions, setDocOptions] = useState<{ id: string; title: string }[] | null>(null);

  const openPicker = useCallback(async (convId: string, mode: "move" | "copy") => {
    setConvMenuOpen(false);
    setPicker({ convId, mode });
    setDocOptions(null);
    try {
      const res = await api.documents.list();
      // The documents endpoint returns a paginated envelope { items, … };
      // tolerate either that or a bare array.
      const raw = (res?.documents ?? res) as unknown;
      const docs = (Array.isArray(raw) ? raw : ((raw as { items?: Document[] })?.items ?? [])) as Document[];
      setDocOptions(docs.map((d) => ({ id: d.id, title: d.title || i18n.t("editor:ask.untitled") })));
    } catch { setDocOptions([]); }
  }, []);

  const moveChat = useCallback(async (convId: string, targetDocId: string | null) => {
    const conv = conversationsRef.current.find((c) => c.id === convId);
    if (!conv?.serverId) return;
    try {
      await api.ai.conversations.move(conv.serverId, targetDocId);
      // Global list: keep the conversation visible, just re-tag its scope.
      commitConversations(conversationsRef.current.map((c) =>
        c.id === convId ? { ...c, documentId: targetDocId ?? null, updatedAt: Date.now() } : c));
    } catch { /* surfaced by no-op; cache unchanged */ }
  }, [commitConversations]);

  const cloneChat = useCallback(async (convId: string, targetDocId: string | null) => {
    const conv = conversationsRef.current.find((c) => c.id === convId);
    if (!conv?.serverId) return;
    try {
      const created = await api.ai.conversations.clone(conv.serverId, { documentId: targetDocId });
      const stored: StoredConversation<Turn> = {
        id: created.id, serverId: created.id, title: created.title,
        documentId: targetDocId ?? null,
        turns: created.messages.map((m) => m.content as Turn), updatedAt: Date.now(), hydrated: true,
      };
      commitConversations([stored, ...conversationsRef.current]);
      commitActiveId(created.id);
    } catch { /* no-op */ }
  }, [commitConversations, commitActiveId]);

  // "Continue here" — attach the active conversation to the open document
  // (reassign its scope). This is the promote/follow move (server `move`).
  const continueHere = useCallback(() => {
    const conv = conversationsRef.current.find((c) => c.id === activeIdRef.current);
    if (conv?.serverId) void moveChat(conv.id, documentId ?? null);
  }, [moveChat, documentId]);

  const onPickTarget = useCallback((targetDocId: string | null) => {
    if (!picker) return;
    if (picker.mode === "move") void moveChat(picker.convId, targetDocId);
    else void cloneChat(picker.convId, targetDocId);
    setPicker(null);
  }, [picker, moveChat, cloneChat]);

  // ── Server sync (server is source of truth; cache is optimistic) ──
  // Fetch ALL of the user's conversations (owner-scoped, not per-document), so
  // history follows across the library and every document — no silo. Each
  // conversation keeps its own `documentId` scope tag for grouping + chips.
  // Skip while keep-alive surface is inactive (syncEnabled=false) — T1.
  // Single-flight via ref (not a `cancelled` closure): StrictMode's dev-only
  // double-invoke runs mount→cleanup→mount synchronously on the same instance,
  // so a `cancelled` flag set by the first cleanup would silently swallow the
  // real fetch's result. The ref survives that cycle and only resets once the
  // request settles, so a later genuine remount (e.g. Flow↔Block switch) still
  // refetches as intended.
  const conversationsListInFlight = useRef<Promise<void> | null>(null);
  useEffect(() => {
    if (!syncEnabled) return;
    if (!conversationsListInFlight.current) {
      conversationsListInFlight.current = (async () => {
        try {
          const serverList = await api.ai.conversations.list(); // no documentId → all of the user's
          const stored: StoredConversation<Turn>[] = serverList.map((s) => ({
            id: s.id, serverId: s.id, title: s.title, turns: [],
            documentId: s.documentId ?? null,
            updatedAt: Date.parse(s.updatedAt) || Date.now(), hydrated: false,
          }));
          setConversations((prev) => {
            // Keep local drafts that haven't been persisted yet (unsynced turns).
            // A draft is worth keeping if it has unsent turns OR the user named it:
            // `newChat` creates nothing server-side, so a thread named before its
            // first message would otherwise be dropped right here.
            const drafts = prev.filter(
              (c) => !c.serverId && (c.turns.length > 0 || c.titleLocked),
            );
            const byId = new Map(stored.map((c) => [c.id, c]));
            // Preserve already-hydrated turns for conversations we've opened.
            for (const c of prev) {
              if (c.serverId && byId.has(c.serverId)) {
                const server = byId.get(c.serverId)!;
                byId.set(c.serverId, {
                  ...server,
                  ...(c.hydrated ? { turns: c.turns, hydrated: true } : null),
                  // The rename PATCH is debounced 700ms; a sync landing inside that
                  // window would otherwise pull the stale server title back over
                  // what the user just typed.
                  ...(c.titleLocked ? { title: c.title, titleLocked: true } : null),
                });
              }
            }
            const merged = [...drafts, ...byId.values()].sort((a, b) => b.updatedAt - a.updatedAt);
            saveConversations(documentId, merged);
            return merged;
          });
        } catch { /* offline → keep localStorage cache */ }
        finally { conversationsListInFlight.current = null; }
      })();
    }
    // Owner-scoped global list — fetch once when sync becomes enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncEnabled]);

  // Hydrate the active thread's messages from the server when first opened.
  // Per-sid single-flight set for the same StrictMode double-invoke reason as
  // the list fetch above.
  const hydratingConvIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!syncEnabled) return;
    if (!activeConv || activeConv.hydrated || !activeConv.serverId) return;
    const sid = activeConv.serverId;
    const localId = activeConv.id;
    if (hydratingConvIds.current.has(sid)) return;
    hydratingConvIds.current.add(sid);
    (async () => {
      try {
        const full = await api.ai.conversations.get(sid);
        const loaded = full.messages.map((m) => m.content as Turn);
        setConversations((prev) => {
          const next = prev.map((c) => (c.id === localId ? { ...c, turns: loaded, hydrated: true } : c));
          saveConversations(documentId, next);
          return next;
        });
      } catch { /* keep cache */ }
      finally { hydratingConvIds.current.delete(sid); }
    })();
  }, [syncEnabled, activeConv, documentId]);

  // Persist turns to the server from send() (deterministic, no effect race).
  const creatingRef = useRef<Promise<string> | null>(null);

  // Ensure the active conversation exists on the server (single-flight create);
  // adopt the server id as the local id so they never diverge.
  const ensureServerConvId = useCallback(async (): Promise<string | null> => {
    const localId = activeIdRef.current;
    const conv = conversationsRef.current.find((c) => c.id === localId);
    if (conv?.serverId) return conv.serverId;
    if (creatingRef.current) return creatingRef.current;
    creatingRef.current = (async () => {
      const created = await api.ai.conversations.create({ documentId: documentId ?? null });
      const remapped = conversationsRef.current.map((c) =>
        c.id === localId ? { ...c, id: created.id, serverId: created.id, hydrated: true } : c);
      commitConversations(remapped);
      if (activeIdRef.current === localId) commitActiveId(created.id);
      return created.id;
    })();
    try { return await creatingRef.current; } finally { creatingRef.current = null; }
  }, [documentId, commitConversations, commitActiveId]);

  const persistTurn = useCallback(async (turn: Turn) => {
    try {
      const sid = await ensureServerConvId();
      if (!sid) return;
      await api.ai.conversations.append(sid, { role: turn.role, content: turn });
    } catch { /* cache holds it; reconciles on next list */ }
  }, [ensureServerConvId]);

  // A prefill sent while the dock was closed waits for the panel (askPrefill).
  const [input, setInput] = useState(() => initialMessage ?? takeAskPrefill() ?? "");
  // "Ask Lilia: improve" from the floating selection toolbar (Handoff 26):
  // prefill the composer with the selected passage when the event fires.
  // The panel is already mounted when open, so a post-mount listener (not
  // just the initialMessage init) is needed.
  useEffect(() => {
    const onImprove = (e: Event) => {
      const msg = (e as CustomEvent<{ message?: string }>).detail?.message;
      if (msg) {
        takeAskPrefill();
        setInput(msg);
      }
    };
    window.addEventListener("lilia:ask-improve", onImprove);
    return () => window.removeEventListener("lilia:ask-improve", onImprove);
  }, []);
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState<string | null>(null);
  const allowance = useAskAllowance();
  const queryClient = useContext(QueryClientContext);
  // Ask Lilia on a figure (TikZ 1c): the one dock, scoped to a figure.
  const { scope: figureScope, live: figureLive } = useFigureScope(documentId);
  const figurePreamble = useDocumentPreamble(documentId);
  const [error, setError] = useState<string | null>(null);
  // The level is the account's (Olivia, 12 Oct): asked once, Simple until answered.
  const askLevel = useAskLevel();
  const proficiency: Proficiency = askLevel.effective;
  const askLevelRef = useRef(askLevel);
  askLevelRef.current = askLevel;
  const [levelAck, setLevelAck] = useState<Proficiency | null>(null);
  const showLevelCard = askLevel.known && askLevel.level === null;
  // "Edit document" mode — when on, the AI edits the open document directly.
  // Defaults ON: editing the document is the primary use of Ask Lilia.
  const [editDocChoice, setEditDoc] = useState(true);
  // Edit mode writes the document; a viewer gets a chat (the API enforces it too).
  const storeCanEdit = useCanEdit();
  const mayEdit = canEditProp ?? storeCanEdit;
  const editDoc = editDocChoice && mayEdit;

  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // "Maths from a photo" (18 Oct): the photo picked here is read as maths as soon as it is ready.
  const mathInputRef = useRef<HTMLInputElement | null>(null);
  const [mathAutoSend, setMathAutoSend] = useState(false);

  // Composer attachments (ds30 §6). Held client-side; the row above the prompt
  // previews them and each is removable. (Transport to the AI request is wired
  // separately once the backend accepts attachments — UI/UX adheres now.)
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = useCallback((files: FileList | File[] | null) => {
    if (!files) return;
    const next: Attachment[] = [];
    // Raster images are held as "uploading" until we've measured them (async);
    // svg has no pixel size and Anthropic won't take it anyway, so skip the guard.
    const toMeasure: { id: string; url: string }[] = [];
    for (const file of Array.from(files)) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const isImage = ATTACH_IMAGE_EXT.includes(ext) || file.type.startsWith("image/");
      const isFile = ATTACH_FILE_EXT.includes(ext);
      const id = `${file.name}-${file.size}-${file.lastModified}`;
      if (!isImage && !isFile) {
        next.push({ id, name: file.name, kind: "file", sizeLabel: formatBytes(file.size), status: "error", error: i18n.t("editor:ask.unsupported") });
        continue;
      }
      const maxBytes = isImage ? ATTACH_MAX_IMAGE_BYTES : ATTACH_MAX_BYTES;
      if (file.size > maxBytes) {
        next.push({ id, name: file.name, kind: isImage ? "image" : "file", sizeLabel: formatBytes(file.size), status: "error", error: i18n.t("editor:ask.tooLarge", { mb: isImage ? "5" : "10" }) });
        continue;
      }
      const isSvg = ext === "svg" || file.type === "image/svg+xml";
      const needsMeasure = isImage && !isSvg;
      const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
      next.push({
        id,
        name: file.name,
        kind: isImage ? "image" : "file",
        sizeLabel: formatBytes(file.size),
        previewUrl,
        status: needsMeasure ? "uploading" : "ready",
        file,
      });
      if (needsMeasure && previewUrl) toMeasure.push({ id, url: previewUrl });
    }
    setAttachments((prev) => {
      const seen = new Set(prev.map((a) => a.id));
      return [...prev, ...next.filter((a) => !seen.has(a.id))];
    });
    // Measure raster images and flip each to ready, or error if too small.
    for (const { id, url } of toMeasure) {
      readImageSize(url)
        .then(({ w, h }) => setAttachments((prev) => prev.map((a) => a.id === id
          ? (Math.min(w, h) >= ATTACH_MIN_IMAGE_EDGE
              ? { ...a, status: "ready" }
              : { ...a, status: "error", error: `Image is too small (${w}×${h}px; min ${ATTACH_MIN_IMAGE_EDGE}×${ATTACH_MIN_IMAGE_EDGE})` })
          : a)))
        .catch(() => setAttachments((prev) => prev.map((a) => a.id === id ? { ...a, status: "error", error: "Couldn't read image" } : a)));
    }
  }, []);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const hit = prev.find((a) => a.id === id);
      if (hit?.previewUrl) URL.revokeObjectURL(hit.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  // Revoke any remaining object URLs on unmount.
  useEffect(() => () => { attachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl)); }, [attachments]);

  // On a phone, the keyboard comes only when asked: not while a new document's
  // starter asks are showing (Olivia, 10 Oct). Desktop focuses as before.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // On a phone the sheet opens without the keyboard while the level question shows
  // (Olivia, 12 Oct): it would cover the choices.
  const levelPending = phoneSheet && (!askLevel.known || askLevel.level === null);
  useEffect(() => { if ((!phoneSheet || autoFocusInput) && !levelPending) inputRef.current?.focus(); }, [levelPending]);
  useEffect(() => {
    // An empty conversation starts at its top (its intro, the level question); a running
    // one follows its newest message.
    if (turns.length === 0) scrollRef.current?.scrollTo({ top: 0 });
    else scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns, loading, showLevelCard]);
  // The level question (Olivia, 12 Oct): in the conversation, above the starter chips.
  const levelCard = showLevelCard ? (
    <AskLevelCard
      onPick={(l) => {
        askLevel.setLevel(l);
        setLevelAck(l);
      }}
      onSkip={() => askLevel.setLevel("beginner")}
    />
  ) : levelAck ? (
    <AskLevelAck level={levelAck} onDone={() => setLevelAck(null)} />
  ) : null;
  useEffect(() => () => abortRef.current?.abort(), []);

  // A challenge in progress: the last answer came from Challenge and the author hasn't left it.
  const [challengeLeft, setChallengeLeft] = useState(false);
  const lastLilia = [...turns].reverse().find((tn) => tn.role === "lilia");
  const inChallenge = !challengeLeft && lastLilia?.role === "lilia" && lastLilia.skillId === CHALLENGE_SKILL;

  const send = useCallback(async (message: string, opts?: { math?: boolean; endChallenge?: boolean }) => {
    // Sending while the level question shows counts as Skip: Simple (Olivia, 12 Oct).
    if (askLevelRef.current.known && askLevelRef.current.level === null) askLevelRef.current.setLevel("beginner");
    const text = message.trim();
    if ((!text && !opts?.math) || loading) return;
    setError(null);
    // Snapshot staged attachments now. Text files are inlined into the message;
    // binary/image files are sent as base64 (handled natively server-side).
    const staged = attachments.filter((a) => a.status === "ready");
    const textStaged = staged.filter(isTextExtractable);
    const binaryStaged = staged.filter((a) => !isTextExtractable(a));
    const fileNames = staged.map((a) => a.name);
    // Read as maths: asked for (the chip, the photo button), or typed about an attached image (18 Oct).
    const readMath = !figureScope && binaryStaged.some((a) => a.kind === "image") && (!!opts?.math || isMathReadIntent(text));
    const userTurn: Turn = { role: "user", text: text || i18n.t("editor:askMath.readThis"), files: fileNames.length ? fileNames : undefined };
    // Prior turns → Ask Lilia remembers the thread (PR-B). `turns` here is the
    // pre-send state (setTurns is async), i.e. everything before this message.
    const history = turns
      .map((tn) => tn.role === "user"
        ? { role: "user" as const, content: tn.text }
        : { role: "assistant" as const, content: tn.reply })
      .filter((h) => h.content && h.content.trim().length > 0);
    setTurns((t) => [...t, userTurn]);
    void persistTurn(userTurn);
    setInput("");
    // Clear the staged attachment row (content already captured in `staged`).
    setAttachments((prev) => { prev.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl)); return []; });
    setLoading(true);

    // Text files inline into the message; binary/image files ride as base64.
    const sentMessage = withAskContext(await buildMessageWithAttachments(text, textStaged), blockContext);
    const outgoingAttachments = await buildAttachments(binaryStaged);

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      // Maths from a photo: read, each formula checked by TeX with this document's preamble; nothing is written.
      if (readMath) {
        const res = await api.ai.ask({
          message: sentMessage, proficiency, documentId, history,
          attachments: outgoingAttachments,
          math: { afterBlockId: useEditorStore.getState().focusedBlockId ?? undefined },
        }, ac.signal);
        const mathReply: Turn = {
          role: "lilia",
          skillName: res.skillName,
          skillId: res.skillId,
          reply: res.reply,
          commands: [],
          model: res.model ?? undefined,
          math: res.math ? { proposal: res.math } : undefined,
        };
        setTurns((t) => [...t, mathReply]);
        void persistTurn(mathReply);
        return;
      }

      // A challenge in progress keeps the author's answers, whatever their words (continueSkill).
      // It asks and assesses, never writes: no Edit mode, no architect.
      if (inChallenge && !figureScope) {
        const res = await api.ai.ask({ message: sentMessage, proficiency, documentId, history, continueSkill: CHALLENGE_SKILL }, ac.signal);
        const challengeTurn: Turn = { role: "lilia", skillName: res.skillName, skillId: res.skillId, reply: res.reply, commands: [], model: res.model ?? undefined };
        setTurns((t) => [...t, challengeTurn]);
        void persistTurn(challengeTurn);
        if (opts?.endChallenge) setChallengeLeft(true);
        return;
      }
      setChallengeLeft(false);

      // Scoped to a TikZ figure: the server reads that figure only and sends
      // back a proposal it compiled. A change waits in the split view as a diff.
      if (documentId && figureScope) {
        const before = figureLive?.source ?? "";
        const intent = intentFor(text, figureScope.blockId ? (figureLive?.state ?? "drawn") : "empty");
        const figure = buildFigureRequest(figureScope, figureLive, intent);
        const res = await api.ai.ask({ message: sentMessage, proficiency, documentId, history, figure }, ac.signal);
        const figTurn: FigureTurn | undefined = res.figure
          ? { proposal: res.figure, blockId: figureScope.blockId, afterBlockId: figureScope.afterBlockId, before }
          : undefined;
        if (figTurn?.blockId && figTurn.proposal.kind === "change") {
          const change = pendingFrom(figTurn, before);
          if (change) {
            setPendingChange(change);
            if (!figureLive?.splitOpen) requestFigureAction(figTurn.blockId, { type: "open-split" });
          }
        }
        const figureReply: Turn = {
          role: "lilia",
          skillName: res.skillName,
          skillId: res.skillId,
          reply: res.reply,
          commands: [],
          model: res.model ?? undefined,
          figure: figTurn,
        };
        setTurns((t) => [...t, figureReply]);
        void persistTurn(figTurn ? { ...figureReply, figure: figureTurnForStorage(figTurn) } : figureReply);
        return;
      }

      // Edit-document mode ON → DIRECT edit: the model writes to the doc via
      // tools in one loop; we reload the editor if anything changed.
      if (documentId && editDoc) {
        // Stream-ish: tell the editor to poll the doc while the AI edits, so
        // blocks appear as they're written (the AI mutates per tool call). The
        // SignalR push path is ready server-side for when the hub is re-enabled.
        window.dispatchEvent(new CustomEvent("lilia:ai-editing", { detail: { documentId, active: true } }));
        try {
          const res = await api.ai.ask({ message: sentMessage, proficiency, documentId, editMode: true, history, attachments: outgoingAttachments.length ? outgoingAttachments : undefined }, ac.signal);
          if (res.documentChanged) {
            window.dispatchEvent(new CustomEvent("lilia:document-applied", {
              detail: { documentId, changedBlockIds: res.changedBlockIds ?? [] },
            }));
          }
          const commands = detectEditorCommands(text, res.reply);
          const liliaTurn: Turn = {
            role: "lilia",
            skillName: res.skillName,
            skillId: res.skillId,
            reply: res.reply,
            commands,
            undoVersionId: res.documentChanged ? res.undoVersionId : undefined,
            documentId,
            citations: res.citations ?? undefined,
            partialApply: res.partialApply === true,
            model: res.model ?? undefined,
          };
          setTurns((t) => [...t, liliaTurn]);
          void persistTurn(liliaTurn);
          // The mobile reader reviews the change ("Lilia changed 2 blocks", Undo all, Keep).
          if (res.documentChanged) {
            window.dispatchEvent(new CustomEvent(AI_CHANGES_EVENT, {
              detail: { documentId, changedBlockIds: res.changedBlockIds ?? [], undoVersionId: res.undoVersionId ?? null, summary: firstSentence(res.reply) },
            }));
          }
        } finally {
          window.dispatchEvent(new CustomEvent("lilia:ai-editing", { detail: { documentId, active: false } }));
        }
        return;
      }

      // Edit mode OFF but a clear build/rewrite request → propose via the
      // Document Architect (preview → Apply card; non-destructive).
      if (documentId && isDocBuildIntent(text)) {
        const history = turns.map((tn) =>
          tn.role === "user"
            ? { role: "user" as const, content: tn.text }
            : { role: "assistant" as const, content: tn.reply },
        );
        const res = await api.ai.architect(
          {
            documentId,
            messages: [
              ...history,
              { role: "user", content: `${sentMessage}\n\n(You are editing my open document. Generate/replace the content directly with block operations; only ask a question if truly blocked.)` },
            ],
          },
          ac.signal,
        );
        const archTurn: Turn = { role: "lilia", skillName: "Document Architect", reply: res.reply, commands: [], ops: res.operations };
        setTurns((t) => [...t, archTurn]);
        void persistTurn(archTurn);
        return;
      }

      const res = await api.ai.ask({ message: sentMessage, proficiency, documentId, history, attachments: outgoingAttachments.length ? outgoingAttachments : undefined }, ac.signal);
      // Surface contextual editor-command chips only when we're grounded in a
      // document — the commands act on the open doc (validate / preview / …).
      const commands = documentId ? detectEditorCommands(text, res.reply) : [];
      const plainTurn: Turn = {
        role: "lilia",
        skillName: res.skillName,
        reply: res.reply,
        commands,
        citations: res.citations ?? undefined,
        model: res.model ?? undefined,
      };
      setTurns((t) => [...t, plainTurn]);
      void persistTurn(plainTurn);
    } catch (e) {
      if (ac.signal.aborted) return;
      // A figure the author may only read, or one that has gone: a message, not a locked dock.
      if (e instanceof AiArchitectLockedError && (FIGURE_LOCK_REASONS as readonly string[]).includes(e.reason as string)) setError(e.message);
      else if (e instanceof AiArchitectLockedError) setLocked(e.message);
      else setError(e instanceof Error ? e.message : i18n.t("editor:ask.snag"));
    } finally {
      if (!ac.signal.aborted) setLoading(false);
      // An answer spends the allowance: the note and card read the new share.
      void queryClient?.invalidateQueries({ queryKey: planQueryKey });
    }
  }, [loading, proficiency, documentId, turns, editDoc, persistTurn, attachments, figureScope, figureLive, queryClient, inChallenge]);

  // A photo picked for maths is sent once it is measured and ready.
  useEffect(() => {
    if (!mathAutoSend || loading) return;
    const images = attachments.filter((a) => a.kind === "image");
    if (images.length === 0 || images.some((a) => a.status === "uploading")) return;
    setMathAutoSend(false);
    if (images.some((a) => a.status === "ready")) void send(input, { math: true });
  }, [mathAutoSend, attachments, loading, send, input]);
  // "Maths from a photo" from elsewhere (the slash menu): open the picker, here or once mounted.
  const pickMathPhoto = useCallback(() => { setMathAutoSend(true); mathInputRef.current?.click(); }, []);
  useEffect(() => {
    if (takeAskMathPhoto()) pickMathPhoto();
    const onPhoto = () => { takeAskMathPhoto(); pickMathPhoto(); };
    window.addEventListener(ASK_MATH_PHOTO_EVENT, onPhoto);
    return () => window.removeEventListener(ASK_MATH_PHOTO_EVENT, onPhoto);
  }, [pickMathPhoto]);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  }, [input, send]);

  // Built once and rendered by whichever layout owns the control: the pane
  // header on a primary surface, the conversation bar when docked. Two
  // copies of eighty lines of menu is how they drift apart.
  // Move/Copy to… — on both headers: in the chat-first layout the dock's header
  // isn't drawn, and the menu closed without the picker ever opening.
  const docPicker = picker && (
    <div
      data-testid="ask-lilia-doc-picker"
      className="absolute left-3 right-3 top-full z-30 mt-1 max-h-80 overflow-y-auto rounded-lg border border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-surface,#ffffff)] py-1 shadow-lg"
    >
      <div className="flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted,#64748b)]">
        <span>{picker.mode === "move" ? "Move to…" : "Copy to…"}</span>
        <button type="button" data-testid="ask-lilia-doc-picker-cancel" onClick={() => setPicker(null)} className="rounded p-0.5 hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"><IconX size={13} /></button>
      </div>
      <button
        type="button"
        data-testid="ask-lilia-doc-picker-general"
        onClick={() => onPickTarget(null)}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[12.5px] text-[var(--lilia-text,#0f172a)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
      >
        <IconMessageCircle size={13} className="text-[var(--lilia-text-muted,#64748b)]" /> General (no document)
      </button>
      {docOptions === null && <div className="px-3 py-2 text-[12px] text-[var(--lilia-text-muted,#64748b)]">{i18n.t("editor:ask.loadingDocs")}</div>}
      {docOptions?.map((d) => (
        <button
          key={d.id}
          type="button"
          data-testid="ask-lilia-doc-picker-item"
          data-doc-id={d.id}
          onClick={() => onPickTarget(d.id)}
          className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-[12.5px] text-[var(--lilia-text,#0f172a)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
        >
          <span className="truncate">{d.title}</span>
          {d.id === documentId && <span className="ml-auto shrink-0 text-[10.5px] text-[var(--lilia-text-muted,#64748b)]">{picker.mode === "copy" ? "duplicate here" : "current"}</span>}
        </button>
      ))}
    </div>
  );

  const conversationMenu = convMenuOpen && (
          <div
            data-testid="ask-lilia-conv-menu"
            // The popover colour, not the theme surface (which can pick up the Primary blue tint): white in
            // light theme as before, the dark paper in dark theme, where the fixed white held white text (3 Nov).
            className="absolute left-3 right-3 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-[10px] border border-[var(--lilia-border,#e4e9f1)] bg-[var(--popover)] py-1 shadow-lg"
          >
            {conversations.length === 0 && (
              <div className="px-3 py-2 text-[12px] text-[var(--lilia-text-muted,#64748b)]">{i18n.t("editor:ask.noConversations")}</div>
            )}
            {[...conversations].sort((a, b) =>
              (((a.documentId ?? null) === curScope ? 0 : 1) - ((b.documentId ?? null) === curScope ? 0 : 1))
              || (b.updatedAt - a.updatedAt)).map((c) => (
              <div
                key={c.id}
                data-testid="ask-lilia-conv-item"
                data-conv-id={c.id}
                data-active={c.id === activeId}
                className={cn(
                  "group flex items-center gap-2 px-2.5 py-1.5 text-[12.5px] bg-[var(--popover)]",
                  // Selected stays plain; only non-selected rows wash on hover.
                  c.id === activeId
                    ? "font-medium"
                    : "hover:bg-[var(--lilia-surface-hover)]",
                )}
              >
                <button
                  type="button"
                  onClick={() => switchChat(c.id)}
                  className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                >
                  <IconMessageCircle size={13} className="shrink-0 text-[var(--lilia-text-muted,#64748b)]" />
                  <span className="truncate text-[var(--lilia-text,#0f172a)]">{c.title || "New chat"}</span>
                  {scopeChip(c.documentId) && (
                    <span className="ml-1 shrink-0 rounded-full bg-[var(--lilia-surface-hover)] px-1.5 py-px text-[10px] text-[var(--lilia-text-muted,#64748b)]">
                      {scopeChip(c.documentId)}
                    </span>
                  )}
                </button>
                <div className="flex shrink-0 items-center gap-0.5 opacity-0 group-hover:opacity-100">
                  {c.serverId && (
                    <>
                      <button
                        type="button"
                        data-testid="ask-lilia-conv-copy"
                        data-conv-id={c.id}
                        onClick={() => void openPicker(c.id, "copy")}
                        aria-label={i18n.t("editor:ask.copyConvLabel")}
                        title={i18n.t("editor:ask.copyConv")}
                        className="rounded p-0.5 text-[var(--lilia-text-muted,#64748b)] hover:text-[var(--lilia-accent,#0f6cbd)]"
                      >
                        <IconCopy size={13} />
                      </button>
                      <button
                        type="button"
                        data-testid="ask-lilia-conv-move"
                        data-conv-id={c.id}
                        onClick={() => void openPicker(c.id, "move")}
                        aria-label={i18n.t("editor:ask.moveConvLabel")}
                        title={i18n.t("editor:ask.moveConv")}
                        className="rounded p-0.5 text-[var(--lilia-text-muted,#64748b)] hover:text-[var(--lilia-accent,#0f6cbd)]"
                      >
                        <IconArrowRight size={13} />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    data-testid="ask-lilia-conv-delete"
                    data-conv-id={c.id}
                    onClick={() => deleteChat(c.id)}
                    aria-label={i18n.t("editor:ask.deleteConv")}
                    className="rounded p-0.5 text-[var(--lilia-text-muted,#64748b)] hover:text-[var(--lilia-danger,#dc2626)]"
                  >
                    <IconTrash size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        );

  return (

    <div
      className={cn(
        "flex h-full flex-col",
        // Primary (chat-first): tinted ground so side margins read as gutter;
        // Side dock keeps a solid surface.
        primarySurface
          ? "bg-[var(--lilia-ask-ground,#f7f9fc)]"
          : "bg-[var(--lilia-surface,#fff)]",
      )}
      style={{ fontFamily: "var(--lilia-font-sans)" }}
      data-testid="ask-lilia-panel"
      data-primary-surface={primarySurface ? "true" : undefined}
    >
      {/* On a phone the sheet brings its own header (New chat, close): this one is hidden. */}
      {!phoneSheet && (<>
      {/* Header — Primary: 48px flush with artifact header (chat-first.dc).
          Side dock: classic denser bar. */}
      <div
        className={cn(
          // relative: the switcher menu hangs off this row on a primary
          // surface, where the conversation bar that used to host it is gone.
          "relative flex shrink-0 items-center justify-between gap-2",
          primarySurface
            ? "border-b border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-ask-ground,#f7f9fc)] px-5"
            : "border-b border-[var(--lilia-border,#e2e8f0)] px-3 py-2.5",
        )}
        style={primarySurface ? { height: 48 } : undefined}
      >
        {primarySurface && conversationMenu}
        {primarySurface && docPicker}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {/* The strip on the right already names this surface, so a sparkle
              beside a document title would be a second voice saying something
              else. It stays only when there is no strip. */}
          {!(primarySurface && headerTabs) && (
            <IconSparkles
              size={16}
              className="shrink-0 text-[var(--lilia-primary-mild,#1C93C8)]"
            />
          )}
          {primarySurface ? (
            /* 1a — the title IS the thread switcher.
               (Design handoff "Chat surface header", 2026-09-11, §2 option 1a
               + §4: context control · spacer · view tabs. Recommended there
               over keeping a labelled New chat button, which §5 makes a
               ≥560px width state of this same control rather than a separate
               design.)

               One click opens the thread list; a second click — or F2 — turns
               the label into a field in place, which is §2 option 1d·A. The
               "+" beside it is New chat. The second header row this replaces
               carried the same three controls and cost ~44px of conversation
               per pane. */
            renamingTitle ? (
              <input
                ref={renameInputRef}
                type="text"
                data-testid={testIds.chatFirst.chatTitleInput}
                aria-label={i18n.t("editor:ask.convName")}
                className={
                  "min-w-0 flex-1 truncate rounded-md border-0 bg-transparent px-1.5 py-0.5 " +
                  "text-[13px] font-semibold text-[var(--lilia-text,#1b2430)] outline-none " +
                  "bg-[var(--lilia-hover)] focus:ring-2 focus:ring-[var(--lilia-primary)]/40"
                }
                defaultValue={activeConv?.title ?? ""}
                maxLength={120}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { commitRename(e.currentTarget.value); }
                  if (e.key === "Escape") { setRenamingTitle(false); }
                }}
                onBlur={(e) => commitRename(e.currentTarget.value)}
              />
            ) : (
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <button
                  type="button"
                  data-testid={testIds.chatFirst.paneContextSwitcher}
                  aria-haspopup="menu"
                  aria-expanded={convMenuOpen}
                  aria-label={i18n.t("editor:ask.convSwitch", { title: activeConv?.title ?? i18n.t("editor:ask.newChat") })}
                  onClick={() => setConvMenuOpen((o) => !o)}
                  onDoubleClick={() => activeConv && setRenamingTitle(true)}
                  onKeyDown={(e) => { if (e.key === "F2" && activeConv) { e.preventDefault(); setRenamingTitle(true); } }}
                  className={
                    "flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 " +
                    "text-[13px] font-semibold text-[var(--lilia-text,#1b2430)] " +
                    "hover:bg-[var(--lilia-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--lilia-primary)]/40"
                  }
                  title={activeConv?.title ?? i18n.t("editor:ask.newChat")}
                >
                  <IconMessageCircle size={13} className="shrink-0 text-[var(--lilia-text-muted,#64748b)]" />
                  <span data-testid={testIds.chatFirst.paneContextTitle} className="truncate">
                    {activeConv?.title || (isLibrary ? i18n.t("editor:ask.askLibrary") : i18n.t("editor:ask.newChat"))}
                  </span>
                  {(activeConv?.turns.length ?? 0) > 0 && (
                    <span style={{ fontVariantNumeric: "tabular-nums" }}
                      className="shrink-0 text-[11px] font-normal text-[var(--lilia-text-muted,#64748b)]">
                      {activeConv?.turns.length}
                    </span>
                  )}
                  <IconChevronDown size={12} className="shrink-0 text-[var(--lilia-text-muted,#64748b)]" />
                </button>
                <button
                  type="button"
                  data-testid={testIds.chatFirst.chatNew}
                  onClick={newChat}
                  aria-label={i18n.t("editor:ask.newChat")}
                  title={i18n.t("editor:ask.newChat")}
                  className={
                    "shrink-0 rounded-md p-1 text-[var(--lilia-text-muted,#64748b)] " +
                    "hover:bg-[var(--lilia-hover)] hover:text-[var(--lilia-text)] " +
                    "focus:outline-none focus:ring-2 focus:ring-[var(--lilia-primary)]/40"
                  }
                >
                  <IconPlus size={14} />
                </button>
              </div>
            )
          ) : (
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-[13.5px] font-semibold text-[var(--lilia-text,#0f172a)]">
                {isLibrary ? i18n.t("editor:ask.askLibrary") : i18n.t("editor:ask.askLilia")}
              </span>
              <span data-testid="ai-context" className="truncate text-[11px] text-[var(--lilia-text-muted,#64748b)]">
                {contextSub}
              </span>
            </div>
          )}
          {primarySurface && (
            <span data-testid="ai-context" className="sr-only">{contextSub}</span>
          )}
          {figureScope && <FigureContextChip scope={figureScope} live={figureLive} />}
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          {headerTabs}
          {/* Proficiency (New/Some/Pro) control removed from chrome — still
              sent as intermediate by default via localStorage / API body.
              Revisit contrast + placement later; the segment cluttered the
              Primary header ("Newsompro"). */}
          {/* Switch this pane's background from where you are reading it — a
              picker two clicks away is the wrong place to judge something you
              decide by looking. */}
          {onToggleSide && (
            <button
              type="button"
              data-testid="ask-lilia-side-toggle"
              onClick={onToggleSide}
              aria-label={side === "left" ? "Dock on the right" : "Dock on the left"}
              title={side === "left" ? "Dock right" : "Dock left"}
              className="rounded p-1 text-[var(--lilia-text-muted,#64748b)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
            >
              {side === "left" ? <IconLayoutSidebarRightExpand size={15} /> : <IconLayoutSidebarLeftExpand size={15} />}
            </button>
          )}
          {!primarySurface && onToggleMode && (
            <button
              type="button"
              data-testid="ask-lilia-mode-toggle"
              onClick={onToggleMode}
              aria-label={mode === "overlay" ? "Dock panel" : "Pop out to overlay"}
              title={mode === "overlay" ? "Dock" : "Pop out"}
              className="rounded p-1 text-[var(--lilia-text-muted,#64748b)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
            >
              {mode === "overlay" ? <IconLayoutSidebarRight size={15} /> : <IconPictureInPicture size={15} />}
            </button>
          )}
          {onBringBack ? (
            <button
              type="button"
              data-testid={testIds.askWindow.bringBack}
              onClick={onBringBack}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium text-[var(--lilia-primary)] hover:bg-[var(--lilia-hover)]"
            >
              <IconArrowBarToLeft size={14} />
              {i18n.t("editor:ask.bringBack")}
            </button>
          ) : onMove || onArrange ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid={testIds.askWindow.move}
                  aria-label={i18n.t("editor:ask.move")}
                  title={i18n.t("editor:ask.move")}
                  className="rounded p-1 text-[var(--lilia-text-muted,#64748b)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
                >
                  <IconAppWindow size={15} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* The arrangement you're not in; nothing here does nothing. */}
                {onArrange && arrangement === "side" && (
                  <DropdownMenuItem data-testid={testIds.askWindow.sideBySide} onSelect={() => onArrange("chat")}>
                    <IconLayoutColumns size={14} className="mr-2" />
                    <span className="flex flex-col leading-tight">
                      <span>{i18n.t("editor:ask.sideBySide")}</span>
                      <span className="text-[11px] text-[var(--lilia-text-muted)]">{i18n.t("editor:ask.halfHalf")}</span>
                    </span>
                  </DropdownMenuItem>
                )}
                {onArrange && arrangement === "chat" && (
                  <DropdownMenuItem data-testid={testIds.askWindow.dock} onSelect={() => onArrange("side")}>
                    <IconLayoutSidebarRight size={14} className="mr-2" /> {i18n.t("editor:ask.dock")}
                  </DropdownMenuItem>
                )}
                {onArrange && arrangement && onMove && <DropdownMenuSeparator />}
                {onMove && (
                  <DropdownMenuItem data-testid={testIds.askWindow.openWindow} onSelect={() => onMove("window")}>
                    <IconAppWindow size={14} className="mr-2" /> {i18n.t("editor:ask.newWindow")}
                  </DropdownMenuItem>
                )}
                {onMove && canKeepOnTop() && (
                  <DropdownMenuItem data-testid={testIds.askWindow.keepOnTop} onSelect={() => onMove("top")}>
                    <IconPictureInPictureOn size={14} className="mr-2" /> {i18n.t("editor:ask.keepOnTop")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {!primarySurface && !onBringBack && (
            <button
              type="button"
              data-testid="ask-lilia-close"
              onClick={onClose}
              aria-label={i18n.t("editor:ask.close")}
              className="rounded p-1 text-[var(--lilia-text-muted,#64748b)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
            >
              <IconX size={16} />
            </button>
          )}
        </div>
      </div>
      </>)}

      {/* Primary layout: conversation + composer stay centered (max ~620px)
          with side gutters that grow as the column widens — never stretch
          edge-to-edge like the side dock (handoff chat-first prototype). */}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          primarySurface && "items-center",
        )}
      >
      <div
        className={cn(
          "flex min-h-0 w-full flex-1 flex-col",
          // Tighter side gutter (~10px less than before) so the chat content
          // reads closer-in, less spaced-out.
          primarySurface && "max-w-[620px] px-2.5 sm:px-3.5",
        )}
      >

      {/* Conversation bar — switch between past chats or start a new one.

          Only when this panel is docked rather than a pane. On a primary
          surface all three of its controls now sit in the pane header: the
          title IS the switcher, "+" is New chat, and renaming happens in
          place (design handoff "Chat surface header" §2 option 1a + 1d).
          Keeping this row as well would put two thread names in one pane,
          which is the duplication the handoff set out to remove — and cost
          the ~44px of conversation it set out to recover. */}
      {!primarySurface && (
      <div
        className={cn(
          "relative flex items-center gap-2 px-3 py-1.5",
          // Primary: sit on tinted ground (no white strip). Side: bordered bar.
          primarySurface
            ? "border-b border-transparent"
            : "border-b border-[var(--lilia-border,#e2e8f0)]",
        )}
        data-testid="ask-lilia-conv-bar"
      >
        <button
          type="button"
          data-testid="ask-lilia-conv-current"
          onClick={() => setConvMenuOpen((o) => !o)}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-[12.5px] text-[var(--lilia-text,#0f172a)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
          title={i18n.t("editor:ask.switchConv")}
        >
          <IconMessageCircle size={14} className="shrink-0 text-[var(--lilia-text-muted,#64748b)]" />
          <span className="truncate font-medium">{activeConv?.title ?? "New chat"}</span>
          {conversations.length > 0 && (
            <span className="shrink-0 text-[11px] text-[var(--lilia-text-muted,#64748b)]">({conversations.length})</span>
          )}
          <IconChevronDown size={13} className="ml-auto shrink-0 text-[var(--lilia-text-muted,#64748b)]" />
        </button>
        <button
          type="button"
          data-testid="ask-lilia-new-chat"
          onClick={newChat}
          className="flex shrink-0 items-center gap-1 rounded-md bg-[var(--lilia-primary,#0A76A4)] px-2.5 py-1 text-[12px] font-medium text-[color:var(--lilia-on-primary)] hover:opacity-90"
          title={i18n.t("editor:ask.startConv")}
        >
          <IconPlus size={13} /> {i18n.t("editor:ask.newChat")}
        </button>
        {conversationMenu}
        {docPicker}
      </div>
      )}

      {/* Edit-document mode — only when grounded in a document. On = every
          request writes to your document (preview → Apply). */}
      {/* Not during a challenge: it asks and assesses, it never writes (API #356). */}
      {documentId && mayEdit && !phoneSheet && !inChallenge && (
        <label
          data-testid="ask-lilia-editdoc-toggle"
          className={cn(
            "flex items-center gap-2 px-3 py-2 text-[12.5px] cursor-pointer transition-colors",
            primarySurface ? "border-b border-transparent" : "border-b",
            editDoc
              ? primarySurface
                ? "bg-[var(--lilia-primary-subtle,#0A76A414)] text-[var(--lilia-text,#0f172a)]"
                : "border-[var(--lilia-accent,#0f6cbd)]/30 bg-[var(--lilia-accent,#0f6cbd)]/8 text-[var(--lilia-text,#0f172a)]"
              : primarySurface
                ? "text-[var(--lilia-text-muted,#64748b)] hover:bg-[var(--lilia-bg-muted,#eef2f8)]"
                : "border-[var(--lilia-border,#e2e8f0)] text-[var(--lilia-text-muted,#64748b)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]",
          )}
        >
          <input
            type="checkbox"
            data-testid="ask-lilia-editdoc-checkbox"
            checked={editDoc}
            onChange={(e) => setEditDoc(e.target.checked)}
            className="h-4 w-4 accent-[var(--lilia-accent,#0f6cbd)]"
          />
          <IconPencil size={14} className={editDoc ? "text-[var(--lilia-accent,#0f6cbd)]" : ""} />
          <span className="font-medium">{i18n.t("editor:ask.editDoc")}</span>
          <span className="text-[11px] text-[var(--lilia-text-muted,#64748b)]">{i18n.t("editor:ask.editDocHint")}</span>
        </label>
      )}

      {/* Scope carry-over: the active chat is from another scope — continue it
          here (promote) or start a fresh one for this context. */}
      {scopeMismatch && (
        <div
          data-testid="ask-lilia-scope-bar"
          className="mx-3 mt-2 flex items-center gap-2 rounded-md border border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-bg-muted,#f8fafc)] px-2.5 py-1.5 text-[11.5px]"
        >
          <span className="min-w-0 flex-1 truncate text-[var(--lilia-text-muted,#64748b)]">
            <Trans i18nKey="editor:ask.fromDoc" values={{ title: docTitleFor(activeConv?.documentId) }} components={{ strong: <span className="font-medium text-[var(--lilia-text,#0f172a)]" /> }} />
          </span>
          <button
            type="button"
            data-testid="ask-lilia-continue-here"
            onClick={continueHere}
            className="shrink-0 rounded px-2 py-0.5 font-medium text-[var(--lilia-accent,#0f6cbd)] hover:bg-[var(--lilia-accent,#0f6cbd)]/10"
          >
            {i18n.t("editor:ask.continueHere")}
          </button>
          <button
            type="button"
            data-testid="ask-lilia-start-fresh"
            onClick={newChat}
            className="shrink-0 rounded px-2 py-0.5 text-[var(--lilia-text-muted,#64748b)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
          >
            New chat
          </button>
        </div>
      )}

      {/* Body */}
      <div
        ref={scrollRef}
        className={cn(
          "flex-1 overflow-y-auto py-3",
          primarySurface ? "px-1 sm:px-0" : "px-3",
        )}
        data-testid="ask-lilia-body"
      >
        {locked ? (
          <LockedState message={locked} />
        ) : turns.length === 0 && figureScope ? (
          <FigureSuggestions live={figureLive} isNew={!figureScope.blockId} onPick={(s) => { setInput(s.prefill); inputRef.current?.focus(); }} />
        ) : turns.length === 0 ? (
          <EmptyState
            levelCard={levelCard}
            library={isLibrary}
            reader={!isLibrary && !mayEdit}
            primarySurface={!!primarySurface}
            chips={!phoneSheet}
            onPick={(s) => { setInput(s); inputRef.current?.focus(); }}
            onMathPhoto={isLibrary || mayEdit ? pickMathPhoto : undefined}
            onChallenge={isLibrary ? undefined : (kind) => void send(i18n.t(`editor:askChallenge.start.${kind}`))}
          />
        ) : (
          <div className={cn("flex flex-col", primarySurface ? "gap-[22px]" : "gap-3")}>
            {turns.map((t, i) =>
              t.role === "user" ? (
                <div
                  key={i}
                  data-testid="ask-lilia-user"
                  className={cn(
                    "self-end max-w-[88%] px-3.5 py-2.5 text-[13.5px] leading-[1.55]",
                    // Primary contract: soft primary wash + dark text (not solid brand pill).
                    // Side dock keeps the denser solid bubble.
                    primarySurface
                      ? "rounded-[14px_14px_4px_14px] bg-[var(--lilia-primary-subtle,#0A76A414)] text-[var(--lilia-text,#1b2430)]"
                      : "rounded-lg bg-[var(--lilia-accent,#0f6cbd)] py-1.5 text-[13px] text-[color:var(--lilia-on-primary)]",
                  )}
                >
                  <div className="whitespace-pre-wrap">{t.text}</div>
                  {t.files && t.files.length > 0 && (
                    <div data-testid="ask-lilia-user-files" className="mt-1 flex flex-wrap gap-1">
                      {t.files.map((f, k) => (
                        <span
                          key={`${f}-${k}`}
                          className={cn(
                            "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px]",
                            primarySurface
                              ? "bg-[var(--lilia-surface,#fff)]/70 text-[var(--lilia-text-secondary,#5a6a7e)]"
                              : "bg-white/20",
                          )}
                        >
                          <IconPaperclip size={10} /> {f}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div key={i} data-testid="ask-lilia-reply" className="flex max-w-[92%] flex-col gap-1">
                  {/* Primary: "✦ Lilia" on ground (no white reply card). */}
                  {primarySurface ? (
                    <div className="mb-1 flex flex-wrap items-center gap-1.5">
                      <IconSparkles size={14} className="text-[var(--lilia-primary-mild,#1C93C8)]" />
                      <span className="text-[12px] font-semibold text-[var(--lilia-text,#1b2430)]">Lilia</span>
                      {t.skillName && (
                        <span
                          data-testid="ask-lilia-skill-chip"
                          className="rounded-full bg-[var(--lilia-bg-muted)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--lilia-text-secondary)]"
                        >
                          via {t.skillName}
                        </span>
                      )}
                      {t.model && (
                        <span
                          data-testid="ask-lilia-model-chip"
                          title={i18n.t("editor:ask.model")}
                          className="rounded-full border border-[var(--lilia-border-strong)] bg-[var(--lilia-surface-alt)] px-2 py-0.5 font-mono text-[10px] font-medium text-[var(--lilia-text-secondary)]"
                        >
                          {formatModelLabel(t.model)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-1.5 self-start">
                      <span
                        data-testid="ask-lilia-skill-chip"
                        className="rounded-full bg-[var(--lilia-bg-muted)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--lilia-text-secondary)]"
                      >
                        via {t.skillName}
                      </span>
                      {t.model && (
                        <span
                          data-testid="ask-lilia-model-chip"
                          title={i18n.t("editor:ask.model")}
                          className="rounded-full border border-[var(--lilia-border-strong)] bg-[var(--lilia-surface-alt)] px-2 py-0.5 font-mono text-[10px] font-medium text-[var(--lilia-text-secondary)]"
                        >
                          {formatModelLabel(t.model)}
                        </span>
                      )}
                    </div>
                  )}
                  <div
                    className={cn(
                      "ask-lilia-prose text-[var(--lilia-text,#0f172a)]",
                      primarySurface
                        ? "text-[13.5px] leading-[1.6]"
                        : "text-[13px] leading-relaxed",
                    )}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents(onApplyLatex)}
                    >
                      {protectInlineMath(t.reply)}
                    </ReactMarkdown>
                  </div>
                  {/* Reference chips — white raised surfaces on blue ground. */}
                  {isLibrary && libraryDocs && libraryDocs.length > 0 && (() => {
                    const refs = findReferencedDocs(t.reply, libraryDocs);
                    if (refs.length === 0) return null;
                    return (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {refs.map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            data-testid="ai-ref"
                            data-doc-id={r.id}
                            onClick={() => navigate(`/d/${r.id}`)}
                            className="inline-flex items-center gap-1.5 rounded-[9px] border border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-surface,#ffffff)] px-2.5 py-1.5 text-[11.5px] text-[var(--lilia-text-secondary)] hover:border-[var(--lilia-primary-mild,#1C93C8)]"
                          >
                            <IconFileText size={12} /> {r.title}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  {t.figure && <FigureProposalView turn={t.figure} preamble={figurePreamble} />}
                  {t.math && <MathProposalView turn={t.math} />}
                  {t.citations && t.citations.length > 0 && <Sources citations={t.citations} />}
                  {t.partialApply && (
                    <div
                      data-testid="ai-partial-apply"
                      className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900"
                      role="status"
                    >
                      {i18n.t("editor:ask.partial")}
                      {t.documentId && (
                        <span className="ml-2 inline-flex align-middle">
                          <RefreshChip documentId={t.documentId} />
                        </span>
                      )}
                    </div>
                  )}
                  {t.commands.length > 0 && <CommandChips commands={t.commands} />}
                  {t.ops && t.ops.length > 0 && documentId && (
                    <ApplyDocCard documentId={documentId} ops={t.ops} primarySurface={!!primarySurface} />
                  )}
                  {t.undoVersionId && t.documentId && (
                    <div className="mt-2 flex items-center gap-2">
                      <UndoChip documentId={t.documentId} versionId={t.undoVersionId} />
                      <RefreshChip documentId={t.documentId} />
                    </div>
                  )}
                </div>
              ),
            )}
            {loading && (
              <div
                className="flex items-center gap-2 text-[12px] text-[var(--lilia-text-muted,#64748b)]"
                data-testid="ai-thinking"
                aria-live="polite"
              >
                {/* ds30 §9d / MICROCOPY: animated ✦ + "Lilia is writing…"
                    (not "Thinking…"/"Loading…"). */}
                <IconSparkles className="ai-pulse h-3.5 w-3.5 text-[var(--lilia-primary-mild,#1C93C8)]" />
                {i18n.t("editor:ask.writing")}
              </div>
            )}
            {figureScope && !loading && (
              <FigureSuggestions live={figureLive} isNew={!figureScope.blockId} onPick={(s) => { setInput(s.prefill); inputRef.current?.focus(); }} />
            )}
            {/* In a conversation that already has messages, the question comes last. */}
            {levelCard}
          </div>
        )}
        {error && (
          <div data-testid="ask-lilia-error" className="mt-2 rounded-md bg-[var(--lilia-danger-bg,#fef2f2)] px-3 py-2 text-[12px] text-[var(--lilia-danger,#dc2626)]">
            {error}
          </div>
        )}
      </div>

      {/* 4f: the month's allowance. Used: a card in place of the box (an answer in progress
          still finishes, since the box goes only after it). Near: one line above the box. */}
      {!locked && allowance.state === "used" && !loading && (
        <AskAllowanceCard resetsAt={allowance.resetsAt} free={allowance.free} />
      )}
      {!locked && allowance.state === "near" && <AskAllowanceNote resetsAt={allowance.resetsAt} />}

      {/* Composer — Primary: white raised card on blue ground (contract).
          Side: full-width dock footer with top border. */}
      {!locked && !(allowance.state === "used" && !loading) && (
        <div
          className={cn(
            "transition-colors",
            primarySurface
              ? cn(
                  "mb-3 rounded-[14px] border border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-surface,#ffffff)] p-2.5 shadow-sm",
                  dragOver && "outline-dashed outline-2 -outline-offset-2 outline-[var(--lilia-primary)]",
                )
              : cn(
                  "border-t border-[var(--lilia-border,#e2e8f0)] p-2",
                  dragOver && "bg-[var(--lilia-primary-subtle)] outline-dashed outline-2 -outline-offset-2 outline-[var(--lilia-primary)]",
                ),
          )}
          onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); if (e.currentTarget === e.target) setDragOver(false); }}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          data-testid="ask-lilia-composer"
        >
          {/* Hidden inputs driven by the attach buttons. */}
          <input
            ref={imageInputRef}
            type="file"
            accept={ATTACH_ACCEPT_IMAGE}
            multiple
            hidden
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={ATTACH_ACCEPT_FILE}
            multiple
            hidden
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
          <input
            ref={mathInputRef}
            data-testid="ask-math-photo-input"
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); else setMathAutoSend(false); e.target.value = ""; }}
          />

          {/* Attachment row (ds30 §6) — hidden when empty; image thumbnails +
              removable file chips, each showing ready / uploading / error. */}
          {attachments.length > 0 && (
            <div data-testid="ai-attachment-row" className="mb-2 flex flex-wrap gap-1.5">
              {attachments.map((a) => (
                <div
                  key={a.id}
                  data-testid="ai-attachment-chip"
                  data-status={a.status}
                  title={a.error ? `${a.name} — ${a.error}` : a.name}
                  className={cn(
                    "group relative flex items-center gap-1.5 rounded-md border py-1 pl-1.5 pr-1 text-[11px]",
                    a.status === "error"
                      ? "border-[var(--lilia-danger,#dc2626)]/40 bg-[var(--lilia-danger-bg,#fef2f2)] text-[var(--lilia-danger,#dc2626)]"
                      : "border-[var(--lilia-border)] bg-[var(--lilia-surface)] text-[var(--lilia-text)]",
                  )}
                >
                  {a.kind === "image" && a.previewUrl ? (
                    <img src={a.previewUrl} alt="" className="h-6 w-6 shrink-0 rounded object-cover" />
                  ) : (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-[var(--lilia-bg-muted)]">
                      {a.kind === "image" ? <IconPhoto size={14} /> : <IconPaperclip size={14} />}
                    </span>
                  )}
                  <span className="max-w-[120px] truncate font-medium">{a.name}</span>
                  <span className="text-[var(--lilia-text-muted)]">{a.status === "uploading" ? "uploading…" : a.sizeLabel}</span>
                  <button
                    type="button"
                    data-testid="ai-attachment-remove"
                    aria-label={`Remove ${a.name}`}
                    onClick={() => removeAttachment(a.id)}
                    className="ml-0.5 rounded p-0.5 text-[var(--lilia-text-muted)] hover:bg-[var(--lilia-hover)] hover:text-[var(--lilia-text)]"
                  >
                    <IconX size={12} />
                  </button>
                </div>
              ))}
              {/* An image is often maths to read (18 Oct): one tap, promoted. */}
              {!figureScope && !loading && attachments.some((a) => a.kind === "image" && a.status === "ready") && (
                <button
                  type="button"
                  data-testid="ask-read-math"
                  onClick={() => void send(input, { math: true })}
                  className="inline-flex items-center gap-1 rounded-md border border-[var(--lilia-primary-mild,#1C93C8)] bg-[var(--lilia-primary-subtle,#0A76A414)] px-2 py-1 text-[11.5px] font-medium text-[var(--lilia-primary-dark,#0a5f85)]"
                >
                  <IconMathFunction size={13} /> {i18n.t("editor:askMath.readChip")}
                </button>
              )}
            </div>
          )}

          {inChallenge && (
            <div data-testid="ask-challenge-bar" className="mb-1.5 flex flex-wrap items-center gap-1.5 rounded-[9px] border border-[var(--lilia-primary-mild,#1C93C8)] bg-[var(--lilia-primary-subtle,#0A76A414)] px-2.5 py-1.5 text-[12px] text-[var(--lilia-primary-dark,#0a5f85)]">
              <span className="font-medium">{i18n.t("editor:askChallenge.on")}</span>
              <span className="flex-1" />
              <button
                type="button"
                data-testid="ask-challenge-end"
                disabled={loading}
                onClick={() => void send(i18n.t("editor:askChallenge.summarise"), { endChallenge: true })}
                className="rounded-[7px] border border-[var(--lilia-primary-mild,#1C93C8)] bg-[var(--lilia-surface,#ffffff)] px-2 py-0.5 font-medium disabled:opacity-60"
              >
                {i18n.t("editor:askChallenge.end")}
              </button>
              <button
                type="button"
                data-testid="ask-challenge-leave"
                onClick={() => setChallengeLeft(true)}
                className="rounded-[7px] px-1.5 py-0.5 text-[var(--lilia-text-muted,#56616e)] hover:underline"
              >
                {i18n.t("editor:askChallenge.leave")}
              </button>
            </div>
          )}
          {blockContext && (
            <div className="mb-1.5 flex">
              <span
                data-testid={testIds.mobile.askContext}
                className="inline-flex items-center gap-1 rounded-full bg-[var(--lilia-primary-subtle,#0A76A414)] px-2.5 py-1 text-[12px] font-medium text-[var(--lilia-primary-dark,#0a5f85)]"
              >
                ¶ {blockContext.location}
                {onClearBlockContext && (
                  <button type="button" aria-label={i18n.t("editor:ask.removeContext")} onClick={onClearBlockContext} className="ml-0.5 rounded p-0.5">
                    <IconX size={12} />
                  </button>
                )}
              </span>
            </div>
          )}
          <div className="relative">
            <Textarea
              ref={inputRef}
              autoFocus={autoFocusInput}
              data-testid="ask-lilia-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={(e) => {
                const files = Array.from(e.clipboardData.files);
                if (files.length) { e.preventDefault(); addFiles(files); }
              }}
              placeholder={
                inChallenge
                  ? i18n.t("editor:askChallenge.placeholder")
                  : documentId && !mayEdit
                  ? i18n.t("editor:ask.phReader")
                  : primarySurface
                  ? (editDoc ? i18n.t("editor:ask.phEditShort") : i18n.t("editor:ask.phAskShort"))
                  : (editDoc ? i18n.t("editor:ask.phEdit") : i18n.t("editor:ask.phAsk"))
              }
              rows={2}
              className={cn(
                "resize-none pb-8 pr-10 text-[13px]",
                // Inside the white Primary composer card, drop the textarea chrome.
                primarySurface && "border-0 bg-transparent shadow-none focus-visible:ring-0",
              )}
            />
            {/* Attach controls — paperclip (data/source/docs) + photo (images). */}
            <div className="absolute bottom-2 left-2 flex items-center gap-0.5">
              <button
                type="button"
                data-testid="ai-attach-file"
                onClick={() => fileInputRef.current?.click()}
                aria-label={i18n.t("editor:ask.attach")}
                title={i18n.t("editor:ask.attachHint")}
                className="rounded-md p-1.5 text-[var(--lilia-text-muted)] hover:bg-[var(--lilia-hover)] hover:text-[var(--lilia-text)]"
              >
                <IconPaperclip size={16} />
              </button>
              <button
                type="button"
                data-testid="ai-attach-image"
                onClick={() => imageInputRef.current?.click()}
                aria-label={i18n.t("editor:ask.image")}
                title={i18n.t("editor:ask.imageHint")}
                className="rounded-md p-1.5 text-[var(--lilia-text-muted)] hover:bg-[var(--lilia-hover)] hover:text-[var(--lilia-text)]"
              >
                <IconPhoto size={16} />
              </button>
            </div>
            <button
              type="button"
              data-testid="ask-lilia-send"
              onClick={() => void send(input)}
              disabled={loading || !input.trim()}
              aria-label={i18n.t("editor:ask.send")}
              className="absolute bottom-2 right-2 rounded-lg bg-[var(--lilia-primary,#0A76A4)] p-1.5 text-[color:var(--lilia-on-primary)] disabled:opacity-40"
            >
              {loading ? <Spinner className="h-4 w-4" /> : <IconSend size={16} />}
            </button>
          </div>
        </div>
      )}
      {!locked && askLevel.known && (
        <div className="-mt-1 mb-2 flex px-1">
          <AskLevelControl level={proficiency} notSet={askLevel.level === null} onChange={(l) => askLevel.setLevel(l)} />
        </div>
      )}
      {/* Not for a reader who can't edit: no changes will appear (Olivia, 10 Oct). */}
      {onMoveToSide && !locked && mayEdit && (
        <p className="mb-3 px-1 text-center text-[11px] leading-snug text-[var(--lilia-text-muted)]">
          {i18n.t("editor:ask.startingPoint")}{" "}
          <button
            type="button"
            data-testid="move-to-side"
            onClick={onMoveToSide}
            className="text-[var(--lilia-primary-mild,#1C93C8)] underline underline-offset-2 hover:text-[var(--lilia-primary)]"
          >
            {i18n.t("editor:ask.toSide")}
          </button>
        </p>
      )}
      </div>{/* /centered column */}
      </div>{/* /primary center wrapper */}
    </div>
  );
}

// Contextual action chips under a reply — clicking one launches the command
// in the editor (validate / preview / export / find) so the user can act on
// what Ask Lilia just discussed without leaving the conversation.
// "Update this document" — preview the architect's proposed block operations
// and apply them to the OPEN document on an explicit click (never silent).
function ApplyDocCard({
  documentId,
  ops,
  primarySurface = false,
}: {
  documentId: string;
  ops: BlockOp[];
  primarySurface?: boolean;
}) {
  const [state, setState] = useState<"idle" | "applying" | "applied" | "error">("idle");
  const s = summarizeArchitectOps(ops);
  const parts = [
    s.added ? i18n.t("editor:ask.added", { n: s.added }) : "",
    s.edited ? i18n.t("editor:ask.edited", { n: s.edited }) : "",
    s.removed ? i18n.t("editor:ask.removed", { n: s.removed }) : "",
    s.moved ? i18n.t("editor:ask.moved", { n: s.moved }) : "",
  ].filter(Boolean).join(" · ");

  const apply = async () => {
    setState("applying");
    try {
      await applyArchitectOps(documentId, ops);
      // Tell the open editor (Block/Flow) to reload its blocks.
      window.dispatchEvent(new CustomEvent("lilia:document-applied", { detail: { documentId } }));
      setState("applied");
    } catch {
      setState("error");
    }
  };

  return (
    <div
      data-testid="ask-lilia-apply-doc"
      className={cn(
        "mt-1.5 rounded-[9px] border p-2.5",
        // Primary: white raised surface (artifact-chip language). Side: soft accent wash.
        primarySurface
          ? "border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-surface,#ffffff)]"
          : "border-[var(--lilia-accent,#0f6cbd)]/30 bg-[var(--lilia-accent,#0f6cbd)]/6",
      )}
    >
      <div className="text-[12px] font-medium text-[var(--lilia-text,#0f172a)]">
        {i18n.t("editor:ask.canUpdate", { what: parts || i18n.t("editor:ask.changes", { count: ops.length }) })}
      </div>
      {state === "applied" ? (
        <div data-testid="ask-lilia-apply-doc-done" className="mt-1.5 text-[12px] font-medium text-[var(--lilia-success,#16a34a)]">
          {i18n.t("editor:ask.applied")}
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            data-testid="ask-lilia-apply-doc-button"
            onClick={() => void apply()}
            disabled={state === "applying"}
            className="rounded-md bg-[var(--lilia-accent,#0f6cbd)] px-3 py-1.5 text-[12px] font-medium text-[color:var(--lilia-on-primary)] hover:opacity-90 disabled:opacity-60"
          >
            {state === "applying" ? i18n.t("editor:ask.applying") : i18n.t("editor:ask.apply")}
          </button>
          {state === "error" && (
            <span className="text-[11.5px] text-[var(--lilia-danger,#dc2626)]">{i18n.t("editor:ask.applyFailed")}</span>
          )}
        </div>
      )}
    </div>
  );
}

// Undo AI changes — restores the version snapshot taken before the AI edited.
function UndoChip({ documentId, versionId }: { documentId: string; versionId: string }) {
  const [state, setState] = useState<"idle" | "undoing" | "done" | "error">("idle");
  const undo = async () => {
    setState("undoing");
    try {
      await api.documents.versions.restore(documentId, versionId);
      window.dispatchEvent(new CustomEvent("lilia:document-applied", { detail: { documentId, changedBlockIds: [] } }));
      setState("done");
    } catch {
      setState("error");
    }
  };
  if (state === "done") {
    return <div data-testid="ask-lilia-undo-done" className="mt-1.5 text-[11.5px] text-[var(--lilia-text-muted,#64748b)]">{i18n.t("editor:ask.reverted")}</div>;
  }
  // White raised chip on blue Primary ground (contrast TBD — keep white for now).
  const chipClass =
    "inline-flex items-center gap-1 rounded-[9px] border border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-surface,#ffffff)] px-2.5 py-1 text-[11.5px] font-medium text-[var(--lilia-text,#1b2430)] hover:border-[var(--lilia-primary-mild,#1C93C8)] disabled:opacity-50";

  return (
    <div className="mt-1.5">
      <button
        type="button"
        data-testid="ask-lilia-undo"
        onClick={() => void undo()}
        disabled={state === "undoing"}
        className={chipClass}
      >
        <IconArrowBackUp size={13} />
        {state === "undoing" ? i18n.t("editor:ask.undoing") : i18n.t("editor:ask.undo")}
      </button>
      {state === "error" && <span className="ml-2 text-[11px] text-[var(--lilia-danger,#dc2626)]">{i18n.t("editor:ask.undoFailed")}</span>}
    </div>
  );
}

function RefreshChip({ documentId }: { documentId: string }) {
  // Soft refresh first (reload store from server via lilia:document-applied);
  // hard reload only if still stuck. Conversation is localStorage-backed.
  const [tried, setTried] = useState(false);
  const refresh = () => {
    if (!tried) {
      window.dispatchEvent(new CustomEvent("lilia:document-applied", { detail: { documentId, changedBlockIds: [] } }));
      setTried(true);
      return;
    }
    window.location.reload();
  };
  // The one RefreshButton, on the raised chip's ground it had before.
  return (
    <span className="mt-1.5 inline-flex rounded-[9px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)]">
      <RefreshButton
        testId="ask-lilia-refresh"
        label={tried ? i18n.t("editor:ask.reloadHint") : i18n.t("editor:ask.refreshHint")}
        text={tried ? i18n.t("editor:ask.reload") : i18n.t("editor:ask.refresh")}
        onRefresh={refresh}
        iconSize={13}
      />
    </span>
  );
}

// Return the bare hostname (sans "www.") as a fallback label when a citation
// has no title.
function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

// Sources cited from live web search — a numbered list of external links under
// the reply, mirroring the reference-chips pattern.
function Sources({ citations }: { citations: WebCitation[] }) {
  return (
    <div
      data-testid="ask-lilia-sources"
      className="mt-2 rounded-md border border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-bg-muted,#f8fafc)] px-2.5 py-2"
    >
      <div className="mb-1 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted,#64748b)]">
        <IconWorld size={12} /> {i18n.t("editor:ask.sources")}
      </div>
      <ol className="flex flex-col gap-1">
        {citations.map((c, i) => (
          <li key={`${c.url}-${i}`} className="flex items-baseline gap-1.5 text-[12px] leading-snug">
            <span className="shrink-0 text-[var(--lilia-text-muted,#64748b)]">{i + 1}.</span>
            <a
              data-testid="ask-lilia-source"
              data-url={c.url}
              href={c.url}
              target="_blank"
              rel="noreferrer"
              title={c.snippet || c.url}
              className="min-w-0 truncate text-[var(--lilia-accent,#0f6cbd)] hover:underline"
            >
              {c.title || hostOf(c.url)}
            </a>
          </li>
        ))}
      </ol>
    </div>
  );
}

function CommandChips({ commands }: { commands: EditorCommand[] }) {
  const [launched, setLaunched] = useState<EditorCommand | null>(null);
  return (
    <div className="mt-1.5 flex flex-col gap-1" data-testid="ask-lilia-commands">
      <div className="flex flex-wrap gap-1.5">
        {commands.map((cmd) => (
          <button
            key={cmd}
            type="button"
            data-testid="ask-lilia-command"
            data-command={cmd}
            onClick={() => { runEditorCommand(cmd); setLaunched(cmd); }}
            className="inline-flex items-center gap-1 rounded-full border border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-surface,#fff)] px-2.5 py-1 text-[11.5px] font-medium text-[var(--lilia-text-secondary,#475569)] transition-colors hover:border-[var(--lilia-accent,#0f6cbd)]/35 hover:text-[var(--lilia-accent,#0f6cbd)]"
          >
            <IconBolt size={12} className="opacity-70" />
            {EDITOR_COMMANDS[cmd].label}
          </button>
        ))}
      </div>
      {launched && (
        <span
          data-testid="ask-lilia-command-launched"
          className="text-[11px] text-[var(--lilia-text-muted,#64748b)]"
        >
          {EDITOR_COMMANDS[launched].launched} →
        </span>
      )}
    </div>
  );
}

function EmptyState({
  onPick,
  levelCard = null,
  library = false,
  reader = false,
  primarySurface = false,
  chips = true,
  onMathPhoto,
  onChallenge,
}: {
  onPick: (s: string) => void;
  /** "Maths from a photo" (18 Oct): shown on a phone too, where the camera is. */
  onMathPhoto?: () => void;
  /** Challenge (API #356): be questioned on this document. Sent at once. */
  onChallenge?: (kind: (typeof CHALLENGES)[number]) => void;
  /** Ask Lilia's one-time level question (or its "Got it" line), above the chips. */
  levelCard?: React.ReactNode;
  library?: boolean;
  reader?: boolean;
  primarySurface?: boolean;
  /** Off on a phone (the user, 11 Oct): they covered the screen. */
  chips?: boolean;
}) {
  const suggestions = (library ? LIBRARY_SUGGESTIONS : reader ? READER_SUGGESTIONS : SUGGESTIONS).map((k) => i18n.t(`editor:ask.suggest.${k}`));
  return (
    <div className="flex flex-col gap-3" data-testid="ask-lilia-empty">
      <p className="text-[13px] text-[var(--lilia-text-muted,#64748b)]">
        {library
          ? i18n.t("editor:ask.introLibrary")
          : reader
          ? i18n.t("editor:ask.introReader")
          : i18n.t("editor:ask.introEditor")}
      </p>
      {levelCard}
      {onMathPhoto && (
        <button
          type="button"
          data-testid="ask-math-photo"
          onClick={onMathPhoto}
          className={cn(
            "inline-flex items-center gap-2 rounded-[9px] border px-2.5 py-2 text-left text-[12.5px] font-medium text-[var(--lilia-primary-dark,#0a5f85)]",
            "border-[var(--lilia-primary-mild,#1C93C8)] bg-[var(--lilia-surface,#ffffff)] hover:bg-[var(--lilia-primary-subtle,#0A76A414)]",
          )}
        >
          <IconCamera size={16} className="shrink-0" />
          <span>
            {i18n.t("editor:askMath.photoButton")}
            <span className="block text-[11.5px] font-normal text-[var(--lilia-text-muted,#56616e)]">{i18n.t("editor:askMath.photoHint")}</span>
          </span>
        </button>
      )}
      {onChallenge && chips && (
        <div data-testid="ask-challenge-group" className="flex flex-col gap-1">
          <span className="text-[11.5px] font-medium text-[var(--lilia-text-muted,#56616e)]">{i18n.t("editor:askChallenge.group")}</span>
          <div className="flex flex-wrap gap-1.5">
            {CHALLENGES.map((k) => (
              <button
                key={k}
                type="button"
                data-testid="ask-challenge-start"
                data-kind={k}
                onClick={() => onChallenge(k)}
                className="rounded-full border border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-surface,#ffffff)] px-2.5 py-1 text-[12px] text-[var(--lilia-text,#0f172a)] hover:border-[var(--lilia-primary-mild,#1C93C8)]"
              >
                {i18n.t(`editor:askChallenge.chip.${k}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      {chips && <div className="flex flex-col gap-1.5">
        {suggestions.map((s) => (
          <button
            key={s}
            type="button"
            data-testid="ask-lilia-suggestion"
            onClick={() => onPick(s)}
            className={cn(
              "rounded-[9px] border px-2.5 py-1.5 text-left text-[12.5px] text-[var(--lilia-text,#0f172a)]",
              // White chips on blue ground in Primary; flat borders in Side dock.
              primarySurface
                ? "border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-surface,#ffffff)] hover:border-[var(--lilia-primary-mild,#1C93C8)]"
                : "border-[var(--lilia-border,#e2e8f0)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]",
            )}
          >
            {s}
          </button>
        ))}
      </div>}
    </div>
  );
}

function LockedState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-center" data-testid="ask-lilia-locked">
      <IconLock size={22} className="text-[var(--lilia-text-muted,#64748b)]" />
      <p className="max-w-[80%] text-[13px] text-[var(--lilia-text-muted,#64748b)]">{message}</p>
    </div>
  );
}

// react-markdown renderers: unwrap <pre> (the fence provides its own box) and
// route fenced code to FenceBlock; inline code gets a subtle chip.
// remark-gfm enables tables / strikethrough / task lists / autolinks — without
// it GFM pipe tables collapse into a single paragraph of raw "|" text (see
// Ask Lilia CV summary replies).
function markdownComponents(onApplyLatex?: (latex: string) => void | Promise<void>) {
  return {
    pre: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
    code: ({ className, children, ...rest }: { className?: string; children?: React.ReactNode }) => {
      const match = /language-(\w+)/.exec(className ?? "");
      const text = String(children ?? "").replace(/\n$/, "");
      // Maths protected from Markdown (protectInlineMath) is drawn, not shown as code.
      const math = protectedMath(text);
      if (math !== null) return <MathText text={math} />;
      if (match || text.includes("\n")) {
        return <FenceBlock lang={match?.[1] ?? ""} code={text} onApplyLatex={onApplyLatex} />;
      }
      return (
        <code className="rounded bg-[var(--lilia-code-bg,#f1f5f9)] px-1 py-0.5 text-[0.88em]" {...rest}>
          {children}
        </code>
      );
    },
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <a href={href} target="_blank" rel="noreferrer" className="text-[var(--lilia-accent,#0f6cbd)] underline">
        {children}
      </a>
    ),
    // Wrap tables for horizontal scroll in the narrow side rail / primary chat.
    table: ({ children }: { children?: React.ReactNode }) => (
      <div className="ask-lilia-table-wrap my-2 max-w-full overflow-x-auto" data-testid="ask-lilia-table">
        <table>{children}</table>
      </div>
    ),
  };
}

function FenceBlock({
  lang,
  code,
  onApplyLatex,
}: {
  lang: string;
  code: string;
  onApplyLatex?: (latex: string) => void | Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const [applied, setApplied] = useState(false);
  const canApply = !!onApplyLatex && ["latex", "tex", "math"].includes(lang.toLowerCase());

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  }, [code]);

  const apply = useCallback(async () => {
    await onApplyLatex?.(code);
    setApplied(true);
    setTimeout(() => setApplied(false), 1500);
  }, [code, onApplyLatex]);

  return (
    <div
      className="my-2 overflow-hidden rounded-md border border-[var(--lilia-border,#e2e8f0)]"
      data-testid="ask-lilia-fence"
      data-fence-lang={lang || "text"}
    >
      <div className="flex items-center justify-between bg-[var(--lilia-bg-muted,#f1f5f9)] px-2 py-1 text-[10.5px] text-[var(--lilia-text-muted,#64748b)]">
        <span>{lang || "code"}</span>
        <div className="flex items-center gap-2">
          {canApply && (
            <button
              type="button"
              data-testid="ask-lilia-apply"
              onClick={() => void apply()}
              className="font-medium text-[var(--lilia-accent,#0f6cbd)] hover:underline"
            >
              {applied ? "Applied" : "Apply"}
            </button>
          )}
          <button
            type="button"
            data-testid="ask-lilia-copy"
            onClick={() => void copy()}
            className="hover:text-[var(--lilia-text,#0f172a)]"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
      {/* LaTeX and LML get syntax colour; anything we have no grammar for
          stays plain rather than half-highlighted by one that does not fit. */}
      {fenceLanguage(lang) ? (
        <CodeFence code={code} lang={lang} />
      ) : (
        <pre className="m-0 overflow-x-auto p-2 text-[12.5px] leading-snug">
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
}
